import { useCallback } from 'react'

import type { InfiniteData } from '@tanstack/react-query'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useOrganizations } from '@/hooks/organization/useOrganizations'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { Organization } from '@/types/Organization'

interface UseActiveOrgResult<T> {
  activeOrg: T
  isLoading: boolean
}

export function useActiveOrg(): UseActiveOrgResult<Organization | undefined>
export function useActiveOrg<T>(
  select: (activeOrg: Organization | undefined) => T,
): UseActiveOrgResult<T>
export function useActiveOrg<T>(
  select?: (activeOrg: Organization | undefined) => T,
): UseActiveOrgResult<Organization | undefined | T> {
  const { activeOrgId } = useActiveOrgId()

  const selectActiveOrg = useCallback(
    (data: InfiniteData<PaginatedResponse<Organization>, PaginateRequest>) => {
      const activeOrg = data.pages
        .map((res) => res.data)
        .flat()
        .find((org) => org.id === activeOrgId)

      return select ? select(activeOrg) : activeOrg
    },
    [activeOrgId, select],
  )

  const { data: activeOrg, isLoading } = useOrganizations({
    select: selectActiveOrg,
  })

  return {
    activeOrg,
    isLoading,
  }
}

export const useActiveOrgHasCuiEnabled = () => {
  const { activeOrgId } = useActiveOrgId()

  const selectHasCuiEnabled = useCallback(
    (data: InfiniteData<PaginatedResponse<Organization>, PaginateRequest>) => {
      const org = data.pages
        .map((res) => res.data)
        .flat()
        .find((org) => org.id === activeOrgId)

      return org?.cui_compliance_enabled
    },
    [activeOrgId],
  )

  return useOrganizations({ select: selectHasCuiEnabled })
}
